/**
 * OFFLINE OUTBOX — the heart of offline-first trading.
 *
 * Design (POS_MASTER_PLAN.md §6.2):
 *  - A completed sale is an immutable fact. It is written to the queue FIRST,
 *    then transmitted. The receipt prints from local data, so the customer is
 *    never waiting on the network.
 *  - Each document carries a client-generated UUID which is also its
 *    idempotency key, so retrying is always safe: the server returns the
 *    original instead of duplicating.
 *  - Queued documents survive reload, crash and power loss (localStorage is
 *    synchronous, so there is no window where a sale is "in flight" in memory).
 *  - Flush is ordered by sequence, so stock movements apply deterministically.
 *
 * Note: localStorage is used rather than IndexedDB because it is synchronous —
 * a sale cannot be half-written if the browser dies mid-commit. At ~1KB per
 * sale the 5MB budget holds thousands of queued transactions, far more than a
 * store would accumulate during any realistic outage.
 */

const KEY = 'femi9.outbox';
const SEQ_KEY = 'femi9.outbox.seq';

export type DocKind = 'sale' | 'return';

export interface OutboxDoc {
  seq: number;
  kind: DocKind;
  id: string;
  payload: unknown;
  createdAt: string;
  attempts: number;
  lastError?: string;
}

type Listener = (state: OutboxState) => void;

export interface OutboxState {
  pending: number;
  online: boolean;
  flushing: boolean;
  lastError?: string;
  lastSyncAt?: string;
}

const listeners = new Set<Listener>();
let flushing = false;
let lastError: string | undefined;
let lastSyncAt: string | undefined;

function read(): OutboxDoc[] {
  try {
    return JSON.parse(localStorage.getItem(KEY) ?? '[]') as OutboxDoc[];
  } catch {
    return [];
  }
}

function write(docs: OutboxDoc[]): void {
  localStorage.setItem(KEY, JSON.stringify(docs));
}

function nextSeq(): number {
  const n = Number(localStorage.getItem(SEQ_KEY) ?? '0') + 1;
  localStorage.setItem(SEQ_KEY, String(n));
  return n;
}

export function state(): OutboxState {
  return { pending: read().length, online: navigator.onLine, flushing, lastError, lastSyncAt };
}

function notify(): void {
  const s = state();
  listeners.forEach((l) => l(s));
}

export function subscribe(l: Listener): () => void {
  listeners.add(l);
  l(state());
  return () => listeners.delete(l);
}

/** Queue a document. Returns immediately — the till never blocks on the network. */
export function enqueue(kind: DocKind, id: string, payload: unknown): OutboxDoc {
  const doc: OutboxDoc = {
    seq: nextSeq(), kind, id, payload,
    createdAt: new Date().toISOString(), attempts: 0,
  };
  const docs = read();
  docs.push(doc);
  write(docs);
  notify();
  return doc;
}

export function peek(): OutboxDoc[] {
  return read().sort((a, b) => a.seq - b.seq);
}

function remove(seq: number): void {
  write(read().filter((d) => d.seq !== seq));
}

function recordFailure(seq: number, message: string): void {
  const docs = read();
  const d = docs.find((x) => x.seq === seq);
  if (d) {
    d.attempts += 1;
    d.lastError = message;
    write(docs);
  }
}

const API_BASE = ((import.meta as any).env?.VITE_API_BASE as string | undefined)?.replace(/\/$/, '') ?? '';

const ENDPOINTS: Record<DocKind, string> = {
  sale: `${API_BASE}/api/v1/sales`,
  return: `${API_BASE}/api/v1/returns`,
};

/**
 * Attempt to transmit everything queued, in order.
 * Stops on the first transport failure (offline) but continues past permanent
 * rejections, which are moved aside rather than retried forever.
 */
export async function flush(): Promise<{ sent: number; failed: number; rejected: number }> {
  if (flushing) return { sent: 0, failed: 0, rejected: 0 };
  flushing = true;
  lastError = undefined;
  notify();

  let sent = 0, failed = 0, rejected = 0;
  const token = localStorage.getItem('femi9.token');

  try {
    for (const doc of peek()) {
      try {
        const res = await fetch(ENDPOINTS[doc.kind], {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify(doc.payload),
        });
        const json = await res.json().catch(() => null) as any;

        if (res.ok && json?.ok) {
          remove(doc.seq);
          sent++;
          continue;
        }

        // 4xx that is not auth/rate-limit means the server will never accept
        // this document. Retrying forever would block the whole queue, so it is
        // parked for a human to look at.
        const permanent = res.status >= 400 && res.status < 500 &&
          res.status !== 401 && res.status !== 408 && res.status !== 429;
        const message = json?.error?.message ?? `HTTP ${res.status}`;

        if (permanent) {
          recordFailure(doc.seq, `REJECTED: ${message}`);
          rejected++;
          lastError = `A queued ${doc.kind} was rejected: ${message}`;
        } else {
          recordFailure(doc.seq, message);
          failed++;
          lastError = message;
          break; // transient — stop and preserve ordering
        }
      } catch (err) {
        recordFailure(doc.seq, (err as Error).message);
        failed++;
        lastError = 'Offline — queued for retry';
        break;
      }
    }
    if (sent) lastSyncAt = new Date().toISOString();
  } finally {
    flushing = false;
    notify();
  }
  return { sent, failed, rejected };
}

/** Drop a permanently rejected document (manager action). */
export function discard(seq: number): void {
  remove(seq);
  notify();
}

let timer: number | undefined;

/** Retry on reconnect and on a slow interval while offline. */
export function startAutoFlush(intervalMs = 15_000): void {
  if (timer) return;
  window.addEventListener('online', () => { notify(); void flush(); });
  window.addEventListener('offline', notify);
  timer = window.setInterval(() => {
    if (navigator.onLine && read().length) void flush();
  }, intervalMs);
  if (navigator.onLine && read().length) void flush();
}
